import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'

export default function TypesOfDecksBlog() {
  // Handle client-side navigation
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault()
    window.history.pushState({}, '', href)
    window.dispatchEvent(new Event('popstate'))
    window.scrollTo(0, 0)
  }

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])
  
  // Consolidated Schema Data (Local Business, FAQ, Breadcrumb)
  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://busybeaverconstruction.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Blog",
            "item": "https://busybeaverconstruction.com/blog"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Types of Decks for Your Home in Calgary",
            "item": "https://busybeaverconstruction.com/blog/types-of-decks-calgary"
          }
        ]
      },
      {
        "@type": "LocalBusiness",
        "name": "Busy Beaver Construction",
        "image": "https://busybeaverconstruction.com/assets/images/Decks/Composite Decks/Timbertech-Terrain-multi-level-deck-in-Brown-Oak-trim-and-Silver-Maple-surface.-LED-strip-lighting-on-stairs.jpg",
        "@id": "https://busybeaverconstruction.com",
        "url": "https://busybeaverconstruction.com/blog/types-of-decks-calgary",
        "telephone": "403-286-5400",
        "description": "Professional deck builders in Calgary. Specializing in custom composite, cedar, and pressure-treated wood deck installation.",
        "areaServed": {
          "@type": "City",
          "name": "Calgary",
          "addressRegion": "AB",
          "addressCountry": "CA"
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Deck Installation Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Composite Deck Installation"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Cedar Deck Installation"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Vinyl Deck Installation"
              }
            }
          ]
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Do I need a building permit for a new deck in Calgary?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "The City of Calgary requires a building permit if your deck is attached to the house or rises more than 0.6 meters (2 feet) above grade at any point. Detached, grade-level platforms generally do not require a permit, but must still comply with property line setbacks."
            }
          },
          {
            "@type": "Question",
            "name": "How deep do deck footings need to be in Alberta?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "To prevent structural warping from severe frost heave, deck footings in Calgary must be set below the active frost line. This typically requires concrete piles to be drilled and poured to a minimum depth of 48 inches (4 feet)."
            }
          },
          {
            "@type": "Question",
            "name": "Can I build a deck over my property line or utility right-of-way?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "No. Calgary zoning bylaws strictly prohibit building permanent deck structures over utility right-of-ways (URWs) or outside your legal property boundaries. Obtaining a Real Property Report (RPR) is highly recommended before finalizing any structural layout."
            }
          },
          {
            "@type": "Question",
            "name": "How do Chinook winds affect natural wood decks?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Chinooks cause rapid temperature swings that force wood fibres to expand and contract aggressively. Over time, this accelerates cracking, warping, and checking in cedar and pressure-treated wood if they are not consistently protected with UV-resistant stains and sealants."
            }
          },
          {
            "@type": "Question",
            "name": "What is the lifespan of a composite deck in Calgary?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Premium capped composite decking (such as TimberTech or AZEK), when installed correctly with proper joist spacing, typically lasts 25 to 50 years. The protective polymer shell makes it highly resistant to Calgary's extreme UV rays and freeze-thaw cycles."
            }
          }
        ]
      }
    ]
  };

  return (
    <main className="bg-gray-50">
      <Helmet>
        <title>Types of Decks for Your Home in Calgary | Busy Beaver Construction</title>
        <meta name="description" content="Discover the best types of decks for your home in Calgary. Compare composite, cedar, pressure-treated, and vinyl options built for Calgary's harsh climate." />
        <meta name="keywords" content="types of decks Calgary, composite deck Calgary, cedar deck, pressure-treated wood deck, vinyl decking, deck materials guide Calgary" />
        <link rel="canonical" href="https://busybeaverconstruction.com/blog/types-of-decks-calgary" />
        <meta property="og:title" content="Types of Decks for Your Home in Calgary | Busy Beaver Construction" />
        <meta property="og:description" content="Discover the best types of decks for your home in Calgary. Compare composite, cedar, pressure-treated, and vinyl options built for Calgary's harsh climate." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://busybeaverconstruction.com/blog/types-of-decks-calgary" />
        <meta property="og:image" content="/assets/images/Decks/Composite Decks/Timbertech-Terrain-multi-level-deck-in-Brown-Oak-trim-and-Silver-Maple-surface.-LED-strip-lighting-on-stairs.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* JSON-LD Schema placed OUTSIDE Helmet to ensure rendering */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />

      <Header />
      
      {/* Hero Section */}
      <section className="relative h-[450px] md:h-[500px] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/60 z-10"></div>
        <img 
          src="/assets/images/Decks/Composite Decks/Timbertech-Terrain-multi-level-deck-in-Brown-Oak-trim-and-Silver-Maple-surface.-LED-strip-lighting-on-stairs.jpg"
          alt="Custom multi-level composite deck built for a Calgary home"
          className="absolute inset-0 w-full h-full object-cover loading-eager"
          fetchPriority="high"
        />
        <div className="relative z-20 mt-24 text-center max-w-5xl mx-auto px-4">
          <div className="inline-block bg-[#86efac] rounded py-1 px-6 mb-6">
            <p className="text-black text-sm font-bold tracking-wider uppercase">Materials & Layouts Guide</p>
          </div>
          <h1 className="text-white text-4xl md:text-5xl lg:text-7xl font-primary drop-shadow-lg mb-4 leading-tight italic">
            Types of Decks for Your Home in Calgary: Materials & Layouts Guide
          </h1>
          <p className="text-white/90 text-base md:text-lg font-medium font-accent mt-4">
            Compare composite, cedar, pressure-treated, and vinyl options built for Calgary's harsh climate.
          </p>
        </div>
      </section>
      
      {/* Blog Content Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 relative">
            
            {/* Main Article Content */}
            <article className="lg:col-span-8 space-y-6 text-[17px] text-gray-700 leading-relaxed font-text">
              
              <div className="bg-gray-50 p-6 md:p-8 rounded border-l-4 border-[#86efac] my-6">
                <p className="text-xl text-[#0f172a] leading-relaxed font-bold font-secondary">
                  Selecting the right deck configuration requires balancing material longevity against local climate demands. Calgary homeowners must choose materials and layouts that withstand rapid freeze-thaw cycles, heavy winter snow loads, and intense summer UV exposure.
                </p>
              </div>

  
              <div className="bg-blue-50/50 border border-blue-100 p-5 rounded-md my-6 text-sm text-blue-900 font-text flex gap-4 items-start">
                <i className="fas fa-info-circle text-blue-600 text-xl mt-0.5"></i>
                <p className="m-0 leading-relaxed">
                  <strong>Financial & Safety Notice:</strong> Deck construction involves significant financial investment and strict adherence to structural safety codes. Material prices fluctuate. Always secure a custom quote and ensure your contractor pulls the necessary City of Calgary development and building permits to protect your property value and household safety.
                </p>
              </div>

              <p>
                For 25 years, Busy Beaver Construction, a premier <a href="/" onClick={(e) => handleNavigation(e, '/')} className="text-[#0f172a] font-bold underline decoration-2 decoration-[#86efac]/50 hover:decoration-[#86efac] transition-colors">calgary deck builder</a>, has built custom decks across Calgary, helping property owners maximize their outdoor living spaces with structurally compliant, high-durability designs.
              </p>
              
              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">
                Evaluating Decking Materials for Calgary’s Extreme Climate
              </h2>
              <p>
                Calgary's weather features dramatic temperature swings that place extreme structural stress on outdoor installations. The ideal decking material must resist thermal expansion, moisture absorption, and UV degradation. Below is an engineering and cost breakdown of the primary residential decking materials used throughout the Calgary area. To understand exactly how these specific options endure freezing temperatures and high winds, we highly recommend reading our deep-dive analysis on the <a href="/blog/best-decking-material-calgary-winters" onClick={(e) => handleNavigation(e, '/blog/best-decking-material-calgary-winters')} className="text-[#0f172a] font-bold underline decoration-2 decoration-[#86efac]/50 hover:decoration-[#86efac] transition-colors">best decking material for Calgary winters</a>.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Premium Composite Decking (TimberTech, Trex, AZEK)</h3>
              <p>
                Premium composite decking is the most durable, low-maintenance option for Calgary homes because it completely eliminates warping, splitting, and annual staining. Manufactured from dense combinations of recycled wood fibers and synthetic polymers, premium capped composites resist moisture penetration and UV fading caused by high-altitude sunlight.
              </p>
              <p>
                Busy Beaver Construction calculates a base rate of $44.50 per square foot for premium composite surface installations. When configuring layouts with changes in elevation, matching composite stairs are integrated at $230.00 per unit. As a certified TimberTech Platinum Builder, our installation teams build custom composite structures that stay architecturally stable throughout local seasonal shifts.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Traditional Western Red Cedar Decks</h3>
              <p>
                Natural Western Red Cedar provides excellent structural stability and a classic aesthetic because it contains native oils that naturally resist wood rot, decay, and local insect infestations. Cedar fibers expand and contract uniformly, which reduces the structural twisting common in lesser wood species exposed to changing weather conditions.
              </p>
              <p>
                To preserve the authentic wood tone and prevent the surface from silvering under UV exposure, cedar requires a defensive UV-resistant staining treatment every 2 to 3 years. To complement a cedar platform, property owners frequently integrate premium perimeter upgrades. Structurally sound perimeter options include installing Cedar 4x4 posts at $80.00 per linear foot or upgrading to heavy-duty Cedar 4x6 posts at $84.00 per linear foot.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Cost-Effective Pressure-Treated Wood Decks</h3>
              <p>
                Pressure-treated wood is the most common and budget-friendly decking material in Calgary, utilizing chemical preservatives forced deep into lumber to create an explicit shield against fungal decay and moisture rot. It provides a rugged structural foundation capable of supporting heavy snow loads.
              </p>
              <p>
                Our online cost calculator establishes a baseline price of $31.00 per square foot for standard pressure-treated wood surfaces. Adding matching pressure-treated stairs costs $130.00 per unit. This material pairs with standard vertical picket rails or a robust, closed-in skirting option. Pressure-treated skirting costs $36.00 per linear foot, providing an effective barrier that secures the under-deck area. For a full cost breakdown of how these materials and layouts affect your final quote, read our detailed guide explaining <a href="/blog/5" onClick={(e) => handleNavigation(e, '/blog/5')} className="text-[#0f172a] font-bold underline decoration-2 decoration-[#86efac]/50 hover:decoration-[#86efac] transition-colors">how much it costs to build a deck in Calgary</a>.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Weatherproof Vinyl Decking</h3>
              <p>
                Vinyl decking provides an absolute waterproof barrier, making it the most functional selection for elevated layouts or walk-out basements where dry space is required underneath the deck structure. This single-ply membrane is thermally welded over a plywood substrate, creating a seamless exterior floor that sheds water efficiently.
              </p>
              <p>
                Vinyl surface installations are calculated at a base rate of $38.00 per square foot. This material completely eliminates moisture transmission to the lower level, allowing homeowners to build functional under-deck storage or a covered patio. Vinyl surfaces require no sanding or sealing, needing only a seasonal wash to remove environmental dust and debris.
              </p>

              {/* Added Comparison Table */}
              <h3 className="text-2xl font-bold text-[#0f172a] mt-12 mb-4 font-secondary tracking-tight">Decking Material Comparison Overview</h3>
              <div className="overflow-x-auto my-6 shadow-sm rounded border border-gray-200">
                <table className="w-full text-left border-collapse bg-white text-sm md:text-base">
                  <thead>
                    <tr className="bg-[#0f172a] text-white">
                      <th className="p-3 md:p-4 font-bold font-secondary">Material</th>
                      <th className="p-3 md:p-4 font-bold font-secondary">Base Rate (CAD/sq ft)</th>
                      <th className="p-3 md:p-4 font-bold font-secondary">Key Advantage</th>
                      <th className="p-3 md:p-4 font-bold font-secondary">Best For</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-bold text-gray-900">Premium Composite</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700 whitespace-nowrap">$44.50</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Eliminates warping, splitting, and annual staining.</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Long-term durability, low maintenance.</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-bold text-gray-900">Western Red Cedar</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700 whitespace-nowrap">Varies</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Natural resistance to rot, decay, and insects.</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Classic aesthetic with structured maintenance.</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-bold text-gray-900">Pressure-Treated Wood</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700 whitespace-nowrap">$31.00</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Budget-friendly and rugged structural foundation.</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Cost-effective builds and heavy snow loads.</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-bold text-gray-900">Vinyl Decking</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700 whitespace-nowrap">$38.00</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Absolute waterproof barrier, no sealing needed.</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Elevated layouts, walk-out basements.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">
                Choosing the Right Structural Layout for Your Property
              </h2>
              <p>
                The physical layout of your deck dictates how it interacts with your home's foundation and yard topography. Calgary properties require deliberate structural designs to comply with local zoning rules and handle sloped lots.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Detached Grade-Level Decks</h3>
              <p>
                A grade-level deck is a low-profile platform built directly on or just above the ground surface, making it an ideal choice for homeowners looking to bypass lengthy city permitting processes. By staying low to the ground, these structures reduce wind exposure and distribute structural loads directly across concrete deck blocks or shallow footings.
              </p>
              <p>
                The City of Calgary explicitly states that building permits are generally not required for detached decks that sit under 0.6 meters (2 feet) above grade. To ensure complete privacy on tight suburban lots, these low-profile platforms integrate well with a 6-foot-high custom horizontal slat privacy wall, priced at $135.00 per linear foot. Air circulation is maintained by utilizing traditional lattice skirting, priced at $34.00 per linear foot, which vents moisture away from the joists.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Multi-Level and Attached Raised Decks</h3>
              <p>
                Multi-level and attached raised decks optimize sloping yards and walk-out basements by creating separate outdoor zones connected by safe stair systems. These structures anchor directly to your home's ledger board, requiring precise flashing and frost-compliant footings to prevent shifting during winter freeze-thaw cycles.
              </p>
              <p>
                Because these platforms exceed the 0.6-meter elevation threshold or attach directly to the home, they require full building permits from the City of Calgary before construction begins. Elevated structures must feature robust perimeter guards. Homeowners can install code-compliant Black Aluminum Picket railing at $44.00 per linear foot, or choose premium Glass Railing at $54.50 per linear foot to protect the perimeter while keeping views clear.
              </p>

              {/* Informational Semantic FAQ Section */}
              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">
                Frequently Asked Questions About Calgary Decks
              </h2>
              
              <div className="space-y-4">
                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0">Do I need a building permit for a new deck in Calgary?</h3>
                    <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                      <i className="fas fa-chevron-down"></i>
                    </span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                    The City of Calgary requires a building permit if your deck is attached to the house or rises more than 0.6 meters (2 feet) above grade at any point. Detached, grade-level platforms generally do not require a permit, but must still comply with property line setbacks.
                  </div>
                </details>

                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0">How deep do deck footings need to be in Alberta?</h3>
                    <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                      <i className="fas fa-chevron-down"></i>
                    </span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                    To prevent structural warping from severe frost heave, deck footings in Calgary must be set below the active frost line. This typically requires concrete piles to be drilled and poured to a minimum depth of 48 inches (4 feet).
                  </div>
                </details>

                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0">Can I build a deck over my property line or utility right-of-way?</h3>
                    <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                      <i className="fas fa-chevron-down"></i>
                    </span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                    No. Calgary zoning bylaws strictly prohibit building permanent deck structures over utility right-of-ways (URWs) or outside your legal property boundaries. Obtaining a Real Property Report (RPR) is highly recommended before finalizing any structural layout.
                  </div>
                </details>
                
                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0">How do Chinook winds affect natural wood decks?</h3>
                    <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                      <i className="fas fa-chevron-down"></i>
                    </span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                    Chinooks cause rapid temperature swings that force wood fibres to expand and contract aggressively. Over time, this accelerates cracking, warping, and checking in cedar and pressure-treated wood if they are not consistently protected with UV-resistant stains and sealants.
                  </div>
                </details>

                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0">What is the lifespan of a composite deck in Calgary?</h3>
                    <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                      <i className="fas fa-chevron-down"></i>
                    </span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                    Premium capped composite decking (such as TimberTech or AZEK), when installed correctly with proper joist spacing, typically lasts 25 to 50 years. The protective polymer shell makes it highly resistant to Calgary's extreme UV rays and freeze-thaw cycles.
                  </div>
                </details>
              </div>

              <div className="mt-16 mb-8 border-t border-gray-200 pt-12">
                <h2 className="text-3xl font-bold text-[#0f172a] mb-8 font-secondary tracking-tight">
                  Ready to build your deck with the right materials?
                </h2>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a 
                    href="/appointment" 
                    onClick={(e) => handleNavigation(e, '/appointment')}
                    className="inline-flex justify-center items-center px-8 py-4 bg-[#86efac] text-black font-bold text-[16px] rounded shadow hover:brightness-95 transition-all duration-300 font-accent tracking-wide"
                  >
                    CONTACT US TODAY
                  </a>
                  <a 
                    href="/estimate" 
                    onClick={(e) => handleNavigation(e, '/estimate')}
                    className="inline-flex justify-center items-center px-8 py-4 bg-[#0f172a] text-white font-bold text-[16px] rounded shadow hover:bg-black transition-all duration-300 font-accent tracking-wide"
                  >
                    CALCULATE YOUR PROJECT
                  </a>
                </div>
              </div>

            </article>
            
            <aside className="lg:col-span-4 lg:sticky lg:top-24 h-fit mt-8 lg:mt-6">
              <div className="bg-white rounded shadow-sm border border-gray-100 pb-6">
                <h3 className="text-[22px] font-bold mb-4 font-secondary text-[#0f172a] px-6 pt-6 tracking-tight">Project Inspiration</h3>
                
                <div className="grid grid-cols-1 gap-5 max-h-[850px] overflow-y-auto px-6 custom-scrollbar">
                  {[
                    "/assets/images/Decks/Wood Decks/Simple-ground-level-garden-deck.jpg",
                    "/assets/images/Decks/Composite Decks/Outdoor-hot-tub-area-with-covered-lounging-space.jpg",
                    "/assets/images/Pergola/Modern-Pergola-design-for-outdoor-seating-area.jpg",
                    "/assets/images/Decks/Composite Decks/Timbertech-Terrain-multi-level-deck-in-Brown-Oak-trim-and-Silver-Maple-surface.-LED-strip-lighting-on-stairs.jpg",
                    "/assets/images/Other/Living Spaces/Custom-Outdoor-Kitchen-in-Cedar.jpg"
                  ].map((src, index) => (
                    <div key={index} className="overflow-hidden h-[220px] group cursor-pointer shadow-sm border-2 border-white ring-1 ring-gray-100">
                      <img 
                        src={src}
                        alt={`Inspiration gallery image ${index + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 loading-lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </aside>
            
          </div>
        </div>
      </section>
      
      <Footer />
    </main>
  )
}